import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { insightsAPI, subscriptionAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Lock, 
  Calendar, 
  Target, 
  PenLine, 
  MessageCircle, 
  Lightbulb,
  TrendingUp,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Sparkles,
  Check,
  X,
  RefreshCw,
  Bookmark,
  Trophy,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Insight Section config
const SECTIONS = {
  themes: {
    title: 'Key Themes',
    description: 'Patterns and topics that keep showing up in your journey',
    icon: Bookmark,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  focus_areas: {
    title: 'Current Focus',
    description: "What you're actively working on right now",
    icon: Target,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200'
  },
  reflections: {
    title: 'Open Reflections',
    description: 'Questions and threads worth sitting with',
    icon: MessageCircle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  milestones: {
    title: 'Growth Milestones',
    description: 'Moments of progress worth remembering',
    icon: Trophy,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  }
};

function InsightItem({ item, category, onEdit, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(item.text);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = () => {
    if (editText.trim()) {
      onEdit(item.id, editText.trim());
      setIsEditing(false);
    }
  };

  const config = SECTIONS[category];

  return (
    <>
      <div 
        className={`group p-4 rounded-xl border ${config.borderColor} ${config.bgColor}/30 hover:${config.bgColor}/50 transition-colors`}
        data-testid={`insight-item-${item.id}`}
      >
        {isEditing ? (
          <div className="flex gap-2">
            <Input
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="flex-1"
              autoFocus
            />
            <Button size="sm" onClick={handleSave} className="rounded-full">
              <Check size={16} />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setIsEditing(false); setEditText(item.text); }} className="rounded-full">
              <X size={16} />
            </Button>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm text-foreground flex-1">{item.text}</p>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={() => setIsEditing(true)}
                className="h-7 w-7 rounded-full"
              >
                <Pencil size={14} />
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={() => setShowDeleteConfirm(true)}
                className="h-7 w-7 rounded-full text-destructive hover:text-destructive"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        )}
        {item.source === 'ai' && (
          <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Sparkles size={12} />
            <span>Noticed by Clair</span>
          </div>
        )}
      </div>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove this insight?</DialogTitle>
            <DialogDescription>
              This will permanently remove this item from your insights.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="rounded-full">
              Keep it
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => { onDelete(item.id); setShowDeleteConfirm(false); }}
              className="rounded-full"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InsightSection({ category, items, onAdd, onEdit, onDelete }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState('');

  const config = SECTIONS[category];
  const Icon = config.icon;

  const handleAdd = () => {
    if (newText.trim()) {
      onAdd(category, newText.trim());
      setNewText('');
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`insight-section-${category}`}>
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center`}>
            <Icon className={config.color} size={20} />
          </div>
          <div>
            <h3 className="font-medium flex items-center gap-2">
              {config.title}
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {items.length}
              </span>
            </h3>
            <p className="text-sm text-muted-foreground">{config.description}</p>
          </div>
        </div>
        {isExpanded ? <ChevronUp size={20} className="text-muted-foreground" /> : <ChevronDown size={20} className="text-muted-foreground" />}
      </div>

      {isExpanded && (
        <div className="space-y-2 pl-13 ml-5 border-l-2 border-border/50">
          {items.map((item) => (
            <InsightItem
              key={item.id}
              item={item}
              category={category}
              onEdit={(id, text) => onEdit(category, id, text)}
              onDelete={(id) => onDelete(category, id)}
            />
          ))}
          
          {isAdding ? (
            <div className="flex gap-2 p-2">
              <Input
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder={`Add a ${config.title.toLowerCase().slice(0, -1)}...`}
                className="flex-1"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              />
              <Button size="sm" onClick={handleAdd} className="rounded-full">
                <Check size={16} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setIsAdding(false); setNewText(''); }} className="rounded-full">
                <X size={16} />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAdding(true)}
              className="w-full justify-start text-muted-foreground hover:text-foreground rounded-xl"
              data-testid={`add-${category}-btn`}
            >
              <Plus size={16} className="mr-2" />
              Add {config.title.toLowerCase().slice(0, -1)}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ suggestion, onAccept, onDismiss }) {
  const categoryMap = {
    theme: 'themes',
    focus_area: 'focus_areas',
    reflection: 'reflections',
    milestone: 'milestones'
  };
  const config = SECTIONS[categoryMap[suggestion.category]];
  const Icon = config?.icon || Sparkles;

  return (
    <div 
      className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-3"
      data-testid={`suggestion-${suggestion.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full ${config?.bgColor || 'bg-primary/10'} flex items-center justify-center flex-shrink-0`}>
          <Icon className={config?.color || 'text-primary'} size={16} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium">{suggestion.text}</p>
          {suggestion.warmth_note && (
            <p className="text-xs text-muted-foreground mt-1 italic">{suggestion.warmth_note}</p>
          )}
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDismiss(suggestion.id)}
          className="rounded-full text-muted-foreground"
        >
          Not quite
        </Button>
        <Button
          size="sm"
          onClick={() => onAccept(suggestion.id)}
          className="rounded-full"
        >
          <Check size={14} className="mr-1" />
          That resonates
        </Button>
      </div>
    </div>
  );
}

export default function ReflectionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [hasInsightAccess, setHasInsightAccess] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [insights, setInsights] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Check access first
      const usageRes = await subscriptionAPI.getUsage();
      const reflectionAccess = usageRes.data?.has_monthly_reflection || false;
      const insightAccess = usageRes.data?.has_insight_tracker || false;
      setHasAccess(reflectionAccess);
      setHasInsightAccess(insightAccess);

      // Load reflection data if has access
      if (reflectionAccess) {
        const response = await axios.get(`${API}/reflection/monthly`);
        setReflection(response.data);
      }

      // Load insights if has access (Supported Path or higher)
      if (insightAccess) {
        const insightsRes = await insightsAPI.getAll().catch(() => null);
        if (insightsRes) {
          setInsights(insightsRes.data);
          setSuggestions(insightsRes.data.pending_suggestions || []);
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setHasAccess(false);
      } else {
        toast.error('Failed to load reflection');
      }
    } finally {
      setLoading(false);
    }
  };

  // Insight handlers
  const handleAddItem = async (category, text) => {
    try {
      await insightsAPI.createItem(category, text);
      toast.success('Insight added');
      loadData();
    } catch (error) {
      toast.error('Failed to add insight');
    }
  };

  const handleEditItem = async (category, itemId, text) => {
    try {
      await insightsAPI.updateItem(category, itemId, text);
      toast.success('Insight updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update insight');
    }
  };

  const handleDeleteItem = async (category, itemId) => {
    try {
      await insightsAPI.deleteItem(category, itemId);
      toast.success('Insight removed');
      loadData();
    } catch (error) {
      toast.error('Failed to delete insight');
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await insightsAPI.analyze();
      setSuggestions(response.data.suggestions || []);
      if (response.data.suggestions?.length > 0) {
        toast.success('Clair noticed some new patterns');
      } else if (response.data.message) {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to analyze insights');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAcceptSuggestion = async (suggestionId) => {
    try {
      await insightsAPI.validateSuggestion(suggestionId, true);
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      toast.success('Added to your insights');
      loadData();
    } catch (error) {
      toast.error('Failed to accept suggestion');
    }
  };

  const handleDismissSuggestion = async (suggestionId) => {
    try {
      await insightsAPI.validateSuggestion(suggestionId, false);
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
    } catch (error) {
      toast.error('Failed to dismiss suggestion');
    }
  };

  // Locked state
  if (!loading && !hasAccess && !hasInsightAccess) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-16" data-testid="reflection-locked">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
            <Lock className="text-muted-foreground" size={32} />
          </div>
          <h1 className="text-3xl font-serif mb-3">Insights & Reflection</h1>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            A monthly snapshot of your journey — your goals, journal patterns, 
            conversations with Clair, and the insights that shape your path forward.
          </p>
          <Button onClick={() => navigate('/pricing')} className="rounded-full" data-testid="upgrade-btn">
            Unlock with Supported Path
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="animate-spin text-muted-foreground mb-4" size={32} />
          <p className="text-muted-foreground">Preparing your reflection...</p>
        </div>
      </DashboardLayout>
    );
  }

  const snapshot = reflection?.snapshot;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-8" data-testid="reflection-page">
        {/* Header */}
        <div className="text-center">
          {snapshot?.period && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-2">
              <Calendar size={16} />
              <span>{snapshot.period.start} — {snapshot.period.end}</span>
            </div>
          )}
          <h1 className="text-3xl sm:text-4xl font-serif mb-2">Insights & Reflection</h1>
          <p className="text-muted-foreground">
            A pause to see where you have been, what you're learning, and where you're headed
          </p>
        </div>

        {/* AI Reflection - The Narrative */}
        {reflection?.reflection && (
          <Card className="rounded-2xl p-8 bg-accent/5 border-accent/20" data-testid="ai-reflection">
            <div className="prose prose-sm max-w-none">
              <p className="text-foreground leading-relaxed whitespace-pre-line">
                {reflection.reflection}
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-6 text-right">— Clair</p>
          </Card>
        )}

        {/* No reflection yet - encourage activity */}
        {hasAccess && !reflection?.reflection && (
          <Card className="rounded-2xl p-8 text-center bg-muted/30">
            <Sparkles className="mx-auto mb-4 text-muted-foreground" size={32} />
            <h3 className="font-serif text-lg mb-2">Your reflection is brewing</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              As you journal, set goals, and talk with Clair, a personalized narrative 
              will appear here at the end of the month.
            </p>
          </Card>
        )}

        {/* Monthly Snapshot Grid - Only if has monthly reflection access */}
        {hasAccess && snapshot && (
          <>
            <h2 className="text-xl font-serif text-center text-muted-foreground">This Month's Snapshot</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Goals Card */}
              <Card className="rounded-2xl p-6 shadow-md" data-testid="goals-snapshot">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Target className="text-primary" size={20} />
                  </div>
                  <h3 className="font-serif text-lg">Goals & Action Steps</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Active Goals</span>
                    <span className="font-medium">{snapshot?.goals?.active || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Completed This Month</span>
                    <span className="font-medium text-accent">{snapshot?.goals?.completed_this_month || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Steps Completed</span>
                    <span className="font-medium">
                      {snapshot?.goals?.completed_steps || 0} / {snapshot?.goals?.total_steps || 0}
                    </span>
                  </div>
                  {snapshot?.goals?.total_steps > 0 && (
                    <div className="pt-2">
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-accent transition-all duration-300"
                          style={{ width: `${snapshot?.goals?.steps_completion_rate || 0}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 text-right">
                        {snapshot?.goals?.steps_completion_rate || 0}% action steps complete
                      </p>
                    </div>
                  )}
                </div>
              </Card>

              {/* Journal Card */}
              <Card className="rounded-2xl p-6 shadow-md" data-testid="journal-snapshot">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <PenLine className="text-amber-600" size={20} />
                  </div>
                  <h3 className="font-serif text-lg">Journal</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Entries This Month</span>
                    <span className="font-medium">{snapshot?.journal?.entries_count || 0}</span>
                  </div>
                  {snapshot?.journal?.avg_energy && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Avg Energy Level</span>
                      <span className="font-medium">{snapshot?.journal?.avg_energy}/10</span>
                    </div>
                  )}
                  {snapshot?.journal?.top_moods?.length > 0 && (
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground mb-2">Most Common Moods</p>
                      <div className="flex flex-wrap gap-2">
                        {snapshot.journal.top_moods.map((m, i) => (
                          <span 
                            key={m.mood}
                            className="text-xs bg-muted px-3 py-1 rounded-full"
                          >
                            {m.mood} ({m.count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Conversations Card */}
              <Card className="rounded-2xl p-6 shadow-md" data-testid="conversations-snapshot">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <MessageCircle className="text-teal-600" size={20} />
                  </div>
                  <h3 className="font-serif text-lg">Conversations with Clair</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Messages This Month</span>
                    <span className="font-medium">{snapshot?.conversations?.count || 0}</span>
                  </div>
                  {snapshot?.conversations?.count === 0 ? (
                    <p className="text-xs text-muted-foreground pt-2">
                      Haven't talked to Clair yet this month? She's here when you're ready.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground pt-2">
                      Every conversation helps Clair understand your journey better.
                    </p>
                  )}
                </div>
              </Card>

              {/* Insights Summary Card */}
              <Card className="rounded-2xl p-6 shadow-md" data-testid="insights-snapshot">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Lightbulb className="text-purple-600" size={20} />
                  </div>
                  <h3 className="font-serif text-lg">Insight Tracker</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Key Themes</span>
                    <span className="font-medium">{insights?.themes?.length || snapshot?.insights?.themes || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Focus Areas</span>
                    <span className="font-medium">{insights?.focus_areas?.length || snapshot?.insights?.focus_areas || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Milestones</span>
                    <span className="font-medium text-accent">{insights?.milestones?.length || snapshot?.insights?.milestones || 0}</span>
                  </div>
                </div>
              </Card>
            </div>
          </>
        )}

        {/* Insight Tracker - Full Section */}
        {hasInsightAccess && (
          <>
            <div className="border-t border-border pt-8">
              <h2 className="text-xl font-serif text-center mb-2">What Clair Knows</h2>
              <p className="text-sm text-muted-foreground text-center mb-6">
                A living index of your journey. You decide what stays, what goes, and what matters.
              </p>
            </div>

            {/* AI Suggestions Panel */}
            {suggestions.length > 0 && (
              <Card className="rounded-2xl border-primary/20 bg-primary/5 p-6 space-y-4" data-testid="suggestions-panel">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="text-primary" size={20} />
                  </div>
                  <div>
                    <h3 className="font-medium">Clair noticed something</h3>
                    <p className="text-sm text-muted-foreground">
                      Review these and keep what resonates with you
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {suggestions.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onAccept={handleAcceptSuggestion}
                      onDismiss={handleDismissSuggestion}
                    />
                  ))}
                </div>
              </Card>
            )}

            {/* Main Insights Sections */}
            <Card className="rounded-2xl p-6 space-y-8" data-testid="insights-content">
              {Object.keys(SECTIONS).map((category) => (
                <InsightSection
                  key={category}
                  category={category}
                  items={insights?.[category] || []}
                  onAdd={handleAddItem}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteItem}
                />
              ))}
            </Card>

            {/* Analyze Button */}
            <div className="flex flex-col items-center gap-3 py-4">
              <Button
                variant="outline"
                onClick={handleAnalyze}
                disabled={analyzing}
                className="rounded-full"
                data-testid="analyze-btn"
              >
                {analyzing ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <RefreshCw className="mr-2" size={16} />
                )}
                {analyzing ? 'Clair is reflecting...' : 'Ask Clair to look for patterns'}
              </Button>
              {insights?.last_analysis_at && (
                <p className="text-xs text-muted-foreground">
                  Last analyzed {new Date(insights.last_analysis_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </>
        )}

        {/* Empty State */}
        {(!snapshot?.journal?.entries_count && !snapshot?.goals?.active && !snapshot?.conversations?.count &&
          !insights?.themes?.length && !insights?.focus_areas?.length && 
          !insights?.reflections?.length && !insights?.milestones?.length) && (
          <Card className="rounded-2xl p-8 text-center bg-muted/30">
            <TrendingUp className="mx-auto mb-4 text-muted-foreground" size={32} />
            <h3 className="font-serif text-lg mb-2">Your journey is just beginning</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
              As you journal, set goals, and talk with Clair, this page will fill with 
              insights about your journey.
            </p>
            <div className="text-sm space-y-2 text-muted-foreground">
              <p>• Have conversations with Clair</p>
              <p>• Write in your journal</p>
              <p>• Set goals and track your progress</p>
            </div>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Reflections are updated based on your activity. Keep journaling and talking to Clair.
        </p>
      </div>
    </DashboardLayout>
  );
}
